<template>
    <div>
        <ListData :title="$t('menu.artist')" 
            :dataTable="dataTable" 
            :data="data" 
            :totalPages="totalPages"
            :loading="loading"
            :allowMultipleSelect="true"
            :isRowSelectable="canEditRow"
            @fetchData="fetchData"
            @addNewData="createArtistPage"
            @deleteData="deleteSelected"
             />
        <TagsEditor ref="tagsEditor" />
        <SongListSidePanel ref="songListSidePanel" />
        <ArtistInfoSidePanel ref="artistInfoSidePanel" />
        <TriadCopyrightReportModal ref="reportModal" />
        <TriadResourceShareModal ref="shareModal" />
    </div>
</template>

<script setup>
import { ref, computed, h, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import TriadEditIcon from "@/components/icons/TriadEditIcon.vue";
import TriadRemoveIcon from "@/components/icons/TriadRemoveIcon.vue";
import TriadShareIcon from "@/components/icons/TriadShareIcon.vue";
import TriadResourceShareModal from "@/components/common/TriadResourceShareModal.vue";
import TriadCopyrightReportModal from "@/components/common/TriadCopyrightReportModal.vue";
import { ListBulletIcon, FlagIcon } from '@heroicons/vue/24/outline';
import TagsViewer from '@/components/common/elements/TagsViewer.vue';
import TagsEditor from '@/components/common/TriadTagsEditor.vue';
import Pagination from '@/models/pagination';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import userStore, { verifyIam } from '@/store/user.store';
import { useRouter } from 'vue-router';
import { appEvents } from '@/store/app.events';
import { getLocalizedBio } from '@/service/artistUtils.js';

const router = useRouter();
const { t, locale } = useI18n();
const songListSidePanel = ref(null);
const artistInfoSidePanel = ref(null);
const reportModal = ref(null);
const shareModal = ref(null);

const loading = ref(true);
const reloadingTable = ref(false);
const filter = ref({
    pagination: new Pagination(1,10)
});

const totalPages = ref(0);

const dataTable = computed(() => ({
    columns: [

        { 
            title: t('table.name'), 
            field: 'name', 
            component: (element) => {
                return h('button', {
                    type: 'button',
                    class: 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline font-medium focus:outline-none text-left truncate w-full block cursor-pointer',
                    onClick: (e) => {
                        e.stopPropagation();
                        artistInfoSidePanel.value?.open(element._id || element.id);
                    }
                }, element.name);
            },
            rowStyle: 'w-full md:w-[150px] md:max-w-[150px] font-medium truncate overflow-hidden'
        },
        { 
            title: t('table.bio'), 
            field: 'bio', 
            format: (row) => {
                const bio = getLocalizedBio(row, locale.value);
                return bio.length > 140 ? bio.slice(0, 140) + '...' : bio;
            },
            rowStyle: 'text-pretty hidden lg:table-cell w-[200px] max-w-[200px]'
        },
        { title: t('table.tag'), 
            component: (element) => {
                return h(TagsViewer, { 
                    element: element,
                    tags: element.tags || [],
                    visibleAddButton: canEditRow(element)
                })
            },
            rowStyle: 'text-pretty w-[200px] max-w-[200px] hidden lg:table-cell'},
        { title: t('table.totalSongs'), field: 'totalLyrics', sortable: false, rowStyle: 'w-[75px] max-w-[75px] hidden sm:table-cell text-center'},
        {
            properties: () => ({
                tooltip: t('table.songs')
            }),
            component: () => h(ListBulletIcon, { class: 'max-w-6 max-h-6 text-gray-700 dark:text-gray-300 cursor-pointer' }),
            action: (element) => {
                songListSidePanel.value.openForArtist(element);
            },
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        {
            properties: () => ({
                tooltip: t('report.artistButtonTooltip')
            }),
            component: () => h(FlagIcon, { class: 'max-w-5 max-h-5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 cursor-pointer transition-colors' }),
            action: (element) => {
                reportModal.value?.open(element._id || element.id, element.name, 'artist');
            },
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        {
            properties: () => ({
                tooltip: t('resourceShareModal.shareArtistTitle')
            }),
            component: TriadShareIcon,
            action: (element) => {
                if (!canEditRow(element)) {
                    denyNonOwner();
                    return;
                }
                shareModal.value?.open('artist', element.id || element._id, element.name);
            },
            disabled: (element) => !canEditRow(element),
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        { 
            properties: () => ({
                tooltip: t('table.edit')
            }),
            component: TriadEditIcon,
            action: (element) => {
                router.push({path: `/artist/${element._id}`})
            },
            disabled: (element) => !canEditRow(element),
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        { 
            properties: () => ({
                tooltip: t('table.delete')
            }),
            component: TriadRemoveIcon,
            action: (element) => {
                if (!canEditRow(element)) {
                    denyNonOwner();
                    return;
                }
                deleteData(element._id)
            },
            disabled: (element) => !canEditRow(element),
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        
    ]
}));
const data = ref([])

// Visibility rule: own + public + shared artists are listed; write actions
// (edit/delete) are owner-only, except admins (role ADM/ADMIN or IAM grant
// on '/artist', same criterion as the router guard) who can manage all.
// `owner === false` comes from the backend DTO; a missing flag keeps the
// legacy behavior (fail-open) during transition.
const canManageAll = computed(() => {
    try {
        return verifyIam(userStore.state.userInfo, '/artist');
    } catch {
        return false;
    }
});
const canEditRow = (row) => {
    if (!row) return false;
    if (canManageAll.value) return true;
    return row.owner !== false;
};
const denyNonOwner = () => {
    toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction'), icon: 'warning', ttl: 3000 });
};

const fetchData = async (f) => {
    filter.value = f || filter.value;
    const query = Object.entries(filter.value).map( f => {
            // Multi-tag: repeated `tag=` params (backend AND semantics)
            if (f[0] === 'tag' && Array.isArray(f[1])) {
                return f[1].map(t => `tag=${encodeURIComponent(t)}`).join('&');
            }
            if (f[1] !== null && typeof f[1] === 'object') {
                if (typeof f[1].toString === 'function' && f[1].toString !== Object.prototype.toString) {
                    return f[1].toString()
                } else {
                    return Object.entries(f[1]).map(e => `${e[0]}=${e[1]}`).join('&')
                }
            } else {
                return `${f[0]}=${encodeURIComponent(f[1])}`
            }
        }).join('&');
    const response = await fetch(`/api/artist?${query}`)
    if  (response.status === 200) {
        data.value = await response.json()
        totalPages.value = data.value.totalPages
    } else {
        toastStore.dispatch('addMessage', {message: 'Error fetching data', icon: 'error'})
    }
    loading.value = false;
    reloadingTable.value = false;
}

const deleteData = async (id) => {
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete this artist?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/artist/${id}`, {
            method: 'DELETE'
        })
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: 'Artist deleted', icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: 'Error deleting artist', icon: 'error'})
            reloadingTable.value = false;
        }
    }
}
const createArtistPage = () => {
    router.push({path: `/artist/new`})
}

const deleteSelected = async (elements) => {
    const owned = (elements || []).filter(canEditRow);
    if (owned.length === 0) {
        denyNonOwner();
        return;
    }
    if (owned.length < elements.length) {
        denyNonOwner();
    }
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete all these artists?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/artist/delete`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ids: owned.map(e => e.id || e._id)
            })
        })
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: 'Artists deleted', icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: 'Error deleting artists', icon: 'error'})
            reloadingTable.value = false;
        }
    }  
}

const tagsEditor = ref(null);

const createTag = async (element) => {
    if (element && !canEditRow(element)) {
        denyNonOwner();
        return;
    }
    if (element.bio === undefined && element.name !== undefined && element.title !== undefined) return;
    if (element.bio === undefined && element.name === undefined) return;
    if (!element.tags) element.tags = [];
    const editorTags = element.tags.map(t => typeof t === 'object' && t !== null ? t : { name: t });
    const aiContext = { type: 'artist', name: element.name, bio: element.bio };
    const newTags = await tagsEditor.value.open(editorTags, aiContext);
    if (newTags) {
        toastStore.dispatch('addMessage', {message: 'Saving tags', icon: 'info'});
        const payload = {
            ...element,
            tag: newTags.map(t => typeof t === 'object' ? t.name : t),
            tags: newTags.map(t => typeof t === 'object' ? t.name : t)
        };
        const response = await fetch('/api/artist/' + element._id, {
            method: 'PUT',
            body: JSON.stringify(payload),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: 'Tags updated successfully', icon: 'success'})
            fetchData();
        } else {
            toastStore.dispatch('addMessage', {message: 'Error updating tags', icon: 'error'})
        }
    }
}

onMounted(() => {
    appEvents.on('tagsviewer.createTag', createTag)
})
onUnmounted(() => {
    appEvents.off('tagsviewer.createTag', createTag)
})

</script>